// University of Nevada, Las Vegas: public college page (#2748), official
// facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - UNLV's Common Data Set 2025-2026 (UNLV_common_dataset_2025-2026.pdf,
//    linked from the Office of Decision Support's Common Data Set page): C1
//    applicants/admits/enrollees for Fall 2025; C8 test policy for Fall 2027;
//    C21/C22 early plans; G1/G5 2026-2027 costs. The PDF's figures are drawn
//    into the page and do not survive text extraction, so every number was
//    read from the rendered pages. The C1 residency table is blank. Quirk:
//    G1 gives two nonresident tuition lines ($27,287 out-of-state and $27,577
//    "non-resident"); the out-of-state line is used, from the first-year
//    column (whose required fees, $1,105, are $200 above the all-undergraduate
//    figure).
//  - UNLV Admissions, First-Year Students: admission requirements, the
//    deadlines table, test scores, application fee. The page invites the
//    "Rebel Class of 2031", so it is the page for fall 2027 entry.
//  - College Scorecard, school id 182281 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'UNLV Common Data Set 2025-2026',
        url: 'https://it.unlv.edu/ir/key-metrics/common-data-set',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'UNLV Admissions, First-Year Students',
        url: 'https://www.unlv.edu/admissions/freshman',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?182281-University-of-Nevada-Las-Vegas',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-nevada-las-vegas',
    kbId: 'university_of_nevada_las_vegas',
    name: 'University of Nevada, Las Vegas',
    shortName: 'UNLV',
    location: 'Las Vegas, NV',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (UNLV brand colors); decoration only.
    branding: {
        primary: '#E31837',
        secondary: '#9FA1A4',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_nevada_las_vegas_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 15271,
        admitted: 12820,
        enrolled: 4719,
        source: 'cds',
        where: 'section C1',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'UNLV\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early plans',
        note: 'UNLV has no early decision or early action plan. Its November 15 priority deadline is for financial aid and scholarship consideration, not an early admission round.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Fall application deadline', value: 'June 1' },
        rows: [
            { plan: 'Priority deadline for financial aid and scholarships', deadline: 'November 15', decision: null, binding: false },
            { plan: 'Fall application deadline', deadline: 'June 1', decision: null, binding: false },
        ],
        note: 'For priority consideration for institutional grants and scholarships and an early aid estimate, you must be admitted and have your financial aid applications done by November 15. The application fee is $60, and UNLV takes its own application or the Common App with no preference. UNLV does not publish decision dates.',
        source: 'firstYear',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Test scores are not required for admission or scholarship consideration, but UNLV encourages them for course placement. You meet the admission requirement with a 3.0 GPA in 13 core units (4 English, 3 math, 3 social science, 3 natural science), or an SAT of 1120 (reading and writing plus math), or an ACT composite of 22, or a Nevada Advanced or College and Career Ready high school diploma.',
        source: 'firstYear',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [9374, 27287] },
            { item: 'Required fees', amounts: [1105, 1105] },
            { item: 'Housing (on campus)', amounts: [7170, 7170] },
            { item: 'Food (on-campus meal plan)', amounts: [6024, 6024] },
            { item: 'Books and supplies', amounts: [1240, 1240] },
            { item: 'Transportation', amounts: [1310, 1310] },
            { item: 'Other expenses', amounts: [4516, 4516] },
        ],
        totals: [30739, 48652],
        note: 'For a first-year, full-time undergraduate living on campus, fall and spring.',
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 11062,
            byIncome: [
                { income: '$0 to $30,000', amount: 9250 },
                { income: '$30,001 to $48,000', amount: 9051 },
                { income: '$48,001 to $75,000', amount: 10067 },
                { income: '$75,001 to $110,000', amount: 13684 },
                { income: 'Over $110,000', amount: 16498 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from UNLV\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
